import React from "react";
import { createPortal } from "react-dom";
import { Calendar } from "lucide-react";

const PERIODS = [
  { id: "day", label: "Today" },
  { id: "week", label: "This week" },
  { id: "month", label: "This month" },
  { id: "year", label: "Year" },
  { id: "all", label: "All time" },
];

// `PeriodTabs` renders the standard All / Year / Month / Week / Today pills.
//
// If `onCustomChange` is passed, the left-side "Range" icon becomes clickable
// and opens a small date-picker popover. Selecting a custom range fires
// `onCustomChange({from, to})` and sets `value` to "custom" so callers know
// to send `from` / `to` to the API instead of `period`.
export default function PeriodTabs({
  value,
  onChange,
  customFrom,
  customTo,
  onCustomChange,
}) {
  const [open, setOpen] = React.useState(false);
  const [popPos, setPopPos] = React.useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  const supportsCustom = typeof onCustomChange === "function";
  const isCustom = value === "custom";
  const rangeLabel = isCustom && customFrom && customTo
    ? `${customFrom} → ${customTo}`
    : "Range";

  // Position the popover directly under the Range button on open, and refresh
  // the position on scroll/resize so it stays anchored.
  const updatePos = React.useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPopPos({ top: rect.bottom + 8, left: rect.left });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    updatePos();
    const onScroll = () => updatePos();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, updatePos]);

  // Close on outside-click. Both the button AND the portalled popover count as
  // "inside" so clicking the popover fields doesn't close it.
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const popover = open && supportsCustom ? createPortal(
    <div
      ref={popRef}
      className="p-4"
      data-testid="period-custom-popover"
      style={{
        position: "fixed",
        top: popPos.top,
        left: popPos.left,
        background: "var(--bg)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
        minWidth: 280,
        zIndex: 9999,
      }}
    >
      <div className="label-eyebrow text-[color:var(--fg-3)] mb-2">CUSTOM RANGE</div>
      <div className="flex flex-col gap-2">
        <label className="text-[10px] text-[color:var(--fg-3)]">FROM
          <input
            type="date"
            value={customFrom || ""}
            onChange={(e) => onCustomChange({ from: e.target.value, to: customTo || e.target.value })}
            data-testid="period-custom-from"
            className="period-custom-date w-full mt-1 px-3 py-1.5 text-xs font-mono-num focus:outline-none"
            style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: 8, colorScheme: "dark" }}
          />
        </label>
        <label className="text-[10px] text-[color:var(--fg-3)]">TO
          <input
            type="date"
            value={customTo || ""}
            onChange={(e) => onCustomChange({ from: customFrom || e.target.value, to: e.target.value })}
            data-testid="period-custom-to"
            className="period-custom-date w-full mt-1 px-3 py-1.5 text-xs font-mono-num focus:outline-none"
            style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: 8, colorScheme: "dark" }}
          />
        </label>
        <button
          type="button"
          onClick={() => { onChange("all"); setOpen(false); }}
          data-testid="period-custom-clear"
          className="mt-1 text-[10px] tracking-widest py-1"
          style={{ color: "var(--fg-3)" }}
        >
          CLEAR · BACK TO ALL TIME
        </button>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      className="inline-flex items-center gap-1 p-1"
      data-testid="period-tabs"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "12px",
        backdropFilter: "blur(12px)",
      }}
    >
      {/* Left "Range" pill — clickable when custom is supported */}
      <button
        ref={btnRef}
        type="button"
        onClick={() => supportsCustom && setOpen((v) => !v)}
        data-testid="period-custom-open"
        disabled={!supportsCustom}
        className={`flex items-center gap-2 text-xs font-medium ${supportsCustom ? "cursor-pointer" : "cursor-default"} transition-all`}
        style={{
          color: isCustom ? "#FAFAFA" : "var(--fg-2)",
          background: isCustom ? "var(--accent)" : "transparent",
          borderRadius: 8,
          padding: "6px 12px",
          boxShadow: isCustom ? "var(--accent-glow)" : "none",
        }}
        title={supportsCustom ? "Pick a specific date range" : "Range"}
      >
        <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
        <span className={isCustom ? "font-mono-num text-[10px]" : ""}>{rangeLabel}</span>
      </button>
      {PERIODS.map((p) => {
        const active = value === p.id;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            data-testid={`period-${p.id}`}
            className="px-3 py-1.5 text-xs font-medium transition-all"
            style={{
              background: active ? "var(--accent)" : "transparent",
              color: active ? "#FAFAFA" : "var(--fg-2)",
              borderRadius: 8,
              boxShadow: active ? "var(--accent-glow)" : "none",
            }}
          >
            {p.label}
          </button>
        );
      })}
      {popover}
    </div>
  );
}
